"use client";

/**
 * 统一首页组装层（2026-10-08）。
 *
 * 职责**只有一件**：把 server component 传入的 `HomePayload` 铺到各区块，
 * 并把 client 侧偏好（置顶/排序/访问记录）接到对应区块上。
 *
 * 🔴 它不碰数据获取、不做业务计算 —— 这是「数据请求与视图分离」的落点：
 * 所有 SQL 在 `lib/home/queries.ts`（服务端），所有偏好读写在
 * `lib/home/use-home-prefs.ts`（浏览器），本文件只是接线。
 *
 * ## 栅格（2026-10-08 第 6 轮重排）
 *
 * 按**视觉密度**分四层，而不是按「谁最重要」：
 *   ① 页头（问候 + 搜索 + 账号）
 *   ② 常用功能 —— 紧跟页头的**紧凑横条**（导航层，先看到再用到）
 *   ③ 总览行：**数据看板 + 堆叠产出趋势 + 生产漏斗**三栏并排
 *   ④ 明细行：**待处理 + 准备度 + 运行状态 + agent 控制台**四栏（2026-10-09 行动层改版）
 *
 * ### 🔴 2026-10-09 行动层改版（本轮）
 *
 * 用户点名三处：「分镜数排行 → 换掉」「项目阶段分布 → 并入漏斗」「素材产出趋势 → 按类型堆叠」。
 * 判据是**可行动性**，不是好不好看：
 *   · 分镜数排行是**存量**排名（单调只增、每次打开都一样），
 *     同位置换成**待处理**卡（失败镜头 380 / 失败场景 169 + 按项目落点 + 跳转）；
 *   · 项目阶段分布（14 个项目切 4~5 段的环图）信息密度太低，
 *     并入**生产漏斗**卡的一行图例 —— 同一眼给完，还省下一整格；
 *   · 素材产出趋势从**单条总量**改成**按工序堆叠**（参考图 / 首尾帧 / 视频），
 *     并把它从「估算」升级为「产线节拍」。
 * 新增**准备度**卡（角色立绘 / 道具 / 场景 / 配音四根进度条）——
 * 前置资产与旁路链路的就绪度此前完全不可见（实测配音 1/2317）。
 *
 * 🔴 与「运行状态」卡的分工：那张答「服务活着吗」，行动层三张答「活干得出来吗」。
 * 两者正交 —— 服务全绿而产线大面积失败是常态。
 *
 * ### 🔴 高度差消化总原则（第 8/9 轮定型）
 *
 * 多列行的卡片高度不可能天然相等，差值必须有明确的去处：
 *   · **stretch 对齐底边**（不加 items-start），差值留在卡内；
 *   · 每行指定一张**居中构图的吸收卡** —— 总览行是占比卡（flex-1 +
 *     items-center），明细行是运行状态（列表 flex-1 justify-center），
 *     agent 卡是「事件流 flex-1 + min-h 地板」的生长型吸收器（第 10 轮）；
 *     指标格网格与趋势图也有各自的垂直居中，谁被拉高都对称。
 * 第 4 轮「必须 items-start」的教训针对的是占比内容**沉底**的旧形态；
 * 引入居中吸收卡后规则反转为统一 stretch —— 判据随语义更新，见门禁。
 *
 * ### 🔴 为什么第 7 轮的「占比 / 排行 / 状态三列并排」被推翻
 *
 * 用户截图圈出两张短卡下方的大块空白：三列并排时每列高度由**各自内容**决定
 * （排行 ≈6 行是长卡，占比 ≈1 环 + 图例、状态 ≈4 行都是短卡），
 * `items-start` 下短卡一结束就露出页面底色 —— 高度差没有任何东西消化，
 * 直接破洞在页面上。第 4 轮的教训（stretch 把空白挪进卡内）在这里反用：
 * **先让高度差发生在列内**（两张短卡叠一列，和 ≈ 长卡高度），
 * 再用默认 stretch 把两列拉到等高，残余差值交给占比卡吸收 ——
 * 占比卡是居中构图（body `items-center`），多出的空隙上下对称，读作呼吸感；
 * 而排行/状态是顶部对齐的列表，空隙沉在卡底就是破洞。
 *
 * ### 🔴 为什么第 5 轮的「全宽趋势 + 全宽指标」被推翻
 *
 * 第 5 轮把趋势图从 2/3 栏放回通栏，理由是「横向时序图天生要宽度」。
 * 用户截图的反馈是反的：**不是趋势图太窄，是整页横向拉得太散**。
 * 实测 1440px 下通栏趋势 ≈ 1030px 宽、112px 高，长宽比 9:1，
 * 14 根柱子各 78px、中间大片留白 —— 图在「摊开」而不是在「表达趋势」。
 * 同理 6 张指标卡平铺 1030px，每张 160px 却只印一个数字 + 一个标签。
 *
 * 处方不是「把图压窄」，而是**取消「每个区块都通栏」这个默认**：
 *   · 总览行 1 : 1 两栏 —— 指标卡收进半栏（3 列 × 2 行，1440px 下 ≈220px/张，
 *     与第 4 轮通栏时的单卡宽度同量级，所以卡内排版一个字都不用改），
 *     趋势图同样 ≈680px，14 根柱子各 ≈40px —— 既不摊开也不挤；
 *   · 明细行 0.85 : 1.35 : 1 三栏 —— 占比与状态是短内容，
 *     给它们和 6 行排行榜一样的宽度，多出来的 100px 就是纯空白。
 *     排行反过来需要宽度 —— 项目名是自由文本（「未婚族动画：开局一只鸡」14 字），
 *     窄栏里会被 truncate 吃掉可读性。所以权重按**内容量**分配，不按重要性。
 *
 * 🔴 为什么用 `minmax(0,Nfr)` 而不是 `grid-cols-N`：
 * `1fr` / `3fr` 的最小尺寸是 auto，图表里一个长标签就能把整列撑破
 * （grid 溢出经典坑）。`minmax(0,…)` 允许收缩，配合子元素的
 * `min-w-0 truncate` 才真正不溢出。
 */

import { useTranslations } from "next-intl";
import { AlertTriangle, BarChart3, Database, Filter, Gauge } from "lucide-react";

import { F } from "@/components/studio/ui";
import { useHomePrefs } from "@/lib/home/use-home-prefs";
import type { HomePayload } from "@/lib/home/types";

import { TrendChart } from "./charts";
import { MetricBoard } from "./metric-cards";
import { FunnelCard, PendingCard, ReadinessCard } from "./production-cards";
import { HomeSection } from "./home-primitives";
import { FrequentPanel } from "./quick-actions";
import { RuntimeStatusPanel } from "./runtime-status";
import { WelcomeHeader } from "./welcome-header";
import { AgentConsoleCard } from "@/components/agent/agent-console-card";

export function HomeDashboard({
  payload,
  locale,
  basePath,
  userId,
}: {
  payload: HomePayload;
  locale: string;
  basePath: string;
  userId: string;
}) {
  const t = useTranslations("home");
  // 🔴 根翻译器（无参）：board 里所有 labelKey/unitKey 都带 `home.` 前缀
  //（由 lib/home/queries.ts 生成）。用命名空间翻译器会解析成 `home.home.xxx`，
  // next-intl 找不到就把**键名原样印到界面** —— tsc 与门禁都发现不了，
  // 只有抓 HTML 才看得见（2026-10-08 实测踩过）。
  const tRoot = useTranslations();
  const { prefs, visits, ready, togglePin, setSort, movePin, reset, hideModule, showModule } =
    useHomePrefs();
  const { board } = payload;

  // 空态引导：既无身份也无项目 —— 此时所有图表都是 0，直接展示「去建第一个项目」
  const isBlank = payload.board.totals.projects === 0;

  return (
    <div className="animate-page-in flex flex-col gap-3">
      <WelcomeHeader locale={locale} basePath={basePath} userId={userId} payload={payload} />

      {/* ── ② 常用功能：紧跟页头的紧凑横条 ──
          🔴 第 6 轮从页面末尾上移（2026-10-08 用户截图）。
          首版把它压在 4 个图表之后，理由是「读完看板再找入口」——
          但门户首页的模块入口是**进门第一件要做的事**，藏在页面底部等于没有。
          卡片同时从竖排 101px 压成横条 34px，见 quick-actions.tsx 的说明。 */}
      <FrequentPanel
        locale={locale}
        prefs={prefs}
        visits={visits}
        ready={ready}
        onTogglePin={togglePin}
        onSort={setSort}
        onMove={movePin}
        onHide={hideModule}
        onShow={showModule}
      />

      {/* ── agent 控制台：第 10 轮（2026-10-09）已并入下方 ④ 明细行 ──
          2026-10-09 初版让它独占一行放在「入口」与「指标」之间（当时的理由是
          「操作面要紧跟入口」）。实机排版后它自己就是全页最矮的一块：
          通栏 100% 宽、内容只占中间一段 —— 第 5 轮「每个区块都通栏」的同款病。
          用户点名下移，与分镜榜单、运行状态同排（理由与列宽见 ④ 注释）。
          门禁 home-dashboard-gate 的区块顺序断言只校验 页头 < 入口 < 指标，不受影响。 */}

      {/* ── ③ 总览行：数据看板 + 堆叠产出趋势 + 生产漏斗，三栏并排 ──
          🔴 2026-10-09：原「项目阶段分布」环图被**并入漏斗卡**（见 FunnelCard 的
          阶段图例）。14 个项目切 4~5 段的环图读不出行动差异，而漏斗卡的每一条
          都是「还差多少才成片」。占位大小不变、信息从「占比」升级为「进度 + 阻塞」。
          列宽仍 1 : 1 : 0.9 —— 漏斗每行只有「标签 + 条 + 数」，比环图更省宽。
          高度差消化：本行不用 items-start，默认 stretch 让三卡底边对齐；
          · 指标：卡格网格 content-center（行多余高度上下均分）；
          · 趋势：body justify-center（图垂直居中）；
          · 漏斗：body flex-col justify-center（列表垂直居中，本行主吸收器）。 */}
      <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)]">
        <MetricBoard board={board} />

        <HomeSection
          title={
            <span className="flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5 text-[var(--text-muted)]" aria-hidden />
              {tRoot(board.trend.labelKey)}
            </span>
          }
          // 🔴 正文**顶对齐**（2026-10-09 纵向对齐改版）：本行三张卡高度由 stretch
          //    拉齐，多余高度交给各自的吸收器，而不是每张卡上下均分 ——
          //    后者会让三张卡的**第一行内容不在同一条水平线上**，而纵向对齐看的
          //    正是这条线。本卡的吸收器是绘图区（flex-1，图长高只会更好读）。
          bodyClassName="flex flex-col"
        >
          <TrendChart
            trend={board.trend}
            label={tRoot(board.trend.labelKey)}
            unit={board.trend.unitKey ? tRoot(board.trend.unitKey) : undefined}
            // 数据层的 series.labelKey 带 `home.` 前缀 → 必须走根翻译器
            seriesLabelOf={(key) => tRoot(key)}
            emptyText={
              isBlank
                ? t("charts.blank")
                : t("charts.noTrend", { count: board.trend.points.length })
            }
          />
        </HomeSection>

        <HomeSection
          title={
            <span className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5 text-[var(--text-muted)]" aria-hidden />
              {tRoot(board.funnel.labelKey)}
            </span>
          }
          className="flex-1"
          // 与邻卡同一条首行线：正文顶对齐，多余高度由 5 行漏斗**均分行高**吃掉
          bodyClassName="flex flex-col"
        >
          <FunnelCard
            funnel={board.funnel}
            labelOf={(key) => tRoot(key)}
            emptyText={isBlank ? t("charts.blank") : t("charts.noShare")}
          />
        </HomeSection>
      </div>

      {/* ── ④ 明细行：待处理 + 准备度 + 运行状态 + agent 控制台，四栏并排 ──
          🔴 2026-10-09：原「分镜数排行」被**换成待处理卡**。排行是存量排名
          （单调只增、每次打开都一样、看到「天地崩 1098」不知道下一步做什么），
          同一个视觉位换成「失败镜头 380 / 失败场景 169 + 按项目落点」，
          行动价值完全不同。准备度卡是新加的一格（前置资产 + 配音链路）。
          列宽按**内容量**分配（不按重要性）：待处理需要宽度（项目名是 14 字自由文本）、
          准备度次之（标签 + 分子分母 + 百分比）、状态最省（服务名 + 状态 chip）、
          agent 需要两行标题 → 1.2 : 1 : 0.9 : 1。
          · 窄屏（lg 以下）回落单列；lg 两列；xl 才四栏并排 —— 1440px 下每格约 340px，
            四栏并排仍放得下「项目名 + 明细 + 数」。
          高度差消化机制与总览行同构（stretch 对齐底边 + 各卡自带吸收器）：
          · 待处理 = 本行长卡（分类 + 4 行项目）；
          · 准备度 = 固定 4 行，差值化作条间距；
          · 状态 = 服务列表 flex-1 justify-center 垂直居中；
          · agent = **生长型**：卡根 `flex min-h-0 flex-col`，事件流
            `flex-1 min-h-[13rem] overflow-y-auto`（min-h 地板不能省，
            否则移动端单列时会塌成 0 高，只剩卡头）。 */}
      <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1fr)]">
        <HomeSection
          title={
            <span className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-[var(--text-muted)]" aria-hidden />
              {tRoot(board.pending.labelKey)}
            </span>
          }
          hint={t("pending.hint")}
        >
          <PendingCard
            pending={board.pending}
            labelOf={(key) => tRoot(key)}
            emptyText={t("charts.blank")}
            allClearText={t("pending.allClear")}
          />
        </HomeSection>

        <HomeSection
          title={
            <span className="flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5 text-[var(--text-muted)]" aria-hidden />
              {tRoot(board.readiness.labelKey)}
            </span>
          }
          hint={t("readiness.hint")}
        >
          <ReadinessCard
            readiness={board.readiness}
            labelOf={(key) => tRoot(key)}
            emptyText={t("charts.blank")}
          />
        </HomeSection>

        {/* 🔴 className="flex-1"：状态卡是明细行的**高度差吸收器**，
            列表在卡内垂直居中（见 runtime-status.tsx 的吸收器注释）。 */}
        <RuntimeStatusPanel locale={locale} className="flex-1" />

        {/* agent 控制台：生长型吸收器，见上方注释 */}
        <AgentConsoleCard />
      </div>

      {/* 口径脚注：整页一个出口，说明「数据从哪来、失败了什么」。
          🔴 partialFailure 时必须显眼 —— 部分图表静默为 0 而不声明，
          用户会把「加载失败」误读成「业务数据是 0」。 */}
      <footer className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
        <Database className="h-3 w-3 text-[var(--text-muted)]" aria-hidden />
        <p className={`${F.deco}`}>
          {board.scopeNote.startsWith("home.scope.partialFailure")
            ? t("scope.partialFailure")
            : t("scope.allOk")}
        </p>
        {prefs.pinned.length > 0 ? (
          <button
            type="button"
            onClick={reset}
            className={`${F.deco} underline underline-offset-2 transition-colors hover:text-[var(--text-secondary)]`}
          >
            {t("frequent.resetPins")}
          </button>
        ) : null}
      </footer>
    </div>
  );
}
